const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const zonedTimestampPattern = /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/;
const pacificDayFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function isDateOnlyInputValue(value: string): boolean {
  return dateOnlyPattern.test(value);
}

export function parseDateOnlyInputValue(value: string): Date | null {
  const match = dateOnlyPattern.exec(value);
  if (!match) {
    return null;
  }

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
}

function resolveDateOnlyValue(value: Date | string): Date | null {
  if (typeof value === "string") {
    const dateOnly = parseDateOnlyInputValue(value);
    if (dateOnly) {
      return dateOnly;
    }

    if (!zonedTimestampPattern.test(value)) {
      return null;
    }

    if (!parseDateOnlyInputValue(value.slice(0, 10))) {
      return null;
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateOnly(value: Date | string | null | undefined, locales: Intl.LocalesArgument = "en-US", options: Intl.DateTimeFormatOptions = {}): string {
  const date = value ? resolveDateOnlyValue(value) : null;
  if (!date) {
    return "-";
  }

  return date.toLocaleDateString(locales, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    ...options,
    timeZone: "UTC",
  });
}

export function getDateOnlyAge(value: Date | string, today = new Date()): number | null {
  const birthDate = resolveDateOnlyValue(value);
  if (!birthDate || Number.isNaN(today.getTime())) {
    return null;
  }

  const calendarToday = parseDateOnlyInputValue(getPacificTodayInputValue(today))!;
  let age = calendarToday.getUTCFullYear() - birthDate.getUTCFullYear();
  const birthdayHasPassed = calendarToday.getUTCMonth() > birthDate.getUTCMonth()
    || (calendarToday.getUTCMonth() === birthDate.getUTCMonth() && calendarToday.getUTCDate() >= birthDate.getUTCDate());

  if (!birthdayHasPassed) {
    age -= 1;
  }

  return age;
}

/** HTML date values preserve the UTC calendar fields used by persisted calendar dates. */
export function toDateOnlyInputValue(value: Date | string | null | undefined): string {
  const date = value ? resolveDateOnlyValue(value) : null;
  return date ? date.toISOString().slice(0, 10) : "";
}

/** A widget's local midnight is an adapter value, never the persisted calendar encoding. */
export function toLocalCalendarDate(value: Date): Date {
  return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

export function fromLocalCalendarDate(value: Date): Date {
  return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
}

export function getPacificTodayInputValue(now = new Date()): string {
  const parts = pacificDayFormatter.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(value => value.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
